// src/components/list/UserRow.tsx

import {
  AdminPanelSettingsTwoTone,
  EmojiPeopleTwoTone,
  PersonTwoTone,
  SchoolTwoTone,
} from '@mui/icons-material'
import {
  Avatar,
  Badge,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'

import { UserResource } from '@/types/domain'

import { DDMMYYYYHHMM, getInitials, userLocales } from '../../api'
import { languageConstants as LC } from '../../constants'

type UserRowProps = {
  children: UserResource
  orderby: string
  sx: SxProps<Theme>
}

export const UserRow = ({ children, orderby, sx }: UserRowProps) => {
  const theme = useTheme()
  const success = theme.palette.success.main
  const warning = theme.palette.warning.main
  const bgcolor = theme.palette.primary.main

  const SELECTED_COL = theme.palette.action.selected

  const navigate = useNavigate()
  const { id } = children

  const {
    created_at,
    email,
    firstname,
    image_url,
    is_active,
    label,
    lastname,
    updated_at,
  } = children.attributes

  const anchorOrigin = {
    horizontal: 'right' as const,
    vertical: 'bottom' as const,
  }

  const badgeContent = (
    <span
      style={{
        color: '#666',
        backgroundColor: [warning, success][is_active],
        borderRadius: '50%',
        padding: '1px',
        display: 'inline-block',
      }}
    >
      {label === LC.USERROLE_GUEST && <EmojiPeopleTwoTone />}
      {label === LC.USERROLE_USER && <PersonTwoTone />}
      {label === LC.USERROLE_COACH && <SchoolTwoTone />}
      {label === LC.USERROLE_ADMIN && <AdminPanelSettingsTwoTone />}
    </span>
  )

  const USERNAME = `${firstname} ${lastname}`
  const AVATAR_PHOTO = <Avatar alt={''} src={image_url} />
  const AVATAR_TEXT = <Avatar sx={{ bgcolor }}>{getInitials(USERNAME)}</Avatar>

  const CREATED_AT = new Date(created_at).toLocaleString(
    userLocales,
    DDMMYYYYHHMM
  )

  const UPDATED_AT = new Date(updated_at).toLocaleString(
    userLocales,
    DDMMYYYYHHMM
  )

  const gotoUserEdit = () => navigate(`/users/${id}/edit`)

  const userAvatar = image_url ? (
    <Badge
      anchorOrigin={anchorOrigin}
      badgeContent={badgeContent}
      overlap={'circular'}
    >
      {AVATAR_PHOTO}
    </Badge>
  ) : (
    <Badge
      anchorOrigin={anchorOrigin}
      badgeContent={badgeContent}
      overlap={'circular'}
    >
      {AVATAR_TEXT}
    </Badge>
  )

  return (
    <ListItem sx={{ p: 0, ...sx }}>
      <ListItemButton
        onClick={gotoUserEdit}
        sx={{ p: 0, alignItems: 'stretch' }}
      >
        <ListItemAvatar sx={{ ml: 1 }}>{userAvatar}</ListItemAvatar>
        <ListItemText
          primary={firstname}
          sx={{
            bgcolor: orderby === 'firstname' ? SELECTED_COL : 'transparent',
            cursor: 'pointer',
            m: 0,
            p: 2,
            width: 0.2,
          }}
        />
        <ListItemText
          primary={lastname}
          sx={{
            bgcolor: orderby === 'lastname' ? SELECTED_COL : 'transparent',
            cursor: 'pointer',
            m: 0,
            p: 2,
            width: 0.2,
          }}
        />
        <ListItemText
          primary={id}
          sx={{
            bgcolor: orderby === 'entity_id' ? SELECTED_COL : 'transparent',
            cursor: 'pointer',
            m: 0,
            p: 2,
            width: 0.1,
          }}
        />
        <ListItemText
          primary={email}
          sx={{
            bgcolor: orderby === 'email' ? SELECTED_COL : 'transparent',
            cursor: 'pointer',
            m: 0,
            p: 2,
            width: 0.3,
          }}
        />
        <ListItemText
          primary={CREATED_AT}
          sx={{
            bgcolor: orderby === 'created_at' ? SELECTED_COL : 'transparent',
            cursor: 'pointer',
            m: 0,
            p: 2,
            width: 0.1,
          }}
        />
        <ListItemText
          primary={UPDATED_AT}
          sx={{
            bgcolor: orderby === 'updated_at' ? SELECTED_COL : 'transparent',
            cursor: 'pointer',
            m: 0,
            p: 2,
            width: 0.1,
          }}
        />
      </ListItemButton>
    </ListItem>
  )
}
